import { ArrowDropDown } from '@material-ui/icons';
import { Autocomplete, FilterOptionsState } from '@material-ui/lab';
import React, { useEffect, useMemo, useState } from 'react';
import AgentNetDropdownSelector, { SelectOption } from 'ui-kit/inputs/AgentNetDropdownSelector';
import { FieldValidationError } from 'utilities/validation/validation';
import { AgentNetTextInput } from '../TextField';
import states from '../StateSelectField/states.json';
import territories from '../geo-data/us-territory.json';

export type StateType = {
  name: string;
  abbreviation: string;
  north?: number;
  south?: number;
  east?: number;
  west?: number;
};

export interface EnhancedStateSelectFieldProps {
  value?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
  menuOption?: (value: string) => void;
  onBlur?: (event: React.FocusEvent<any>) => void;
  shouldDisplayAbbreviation?: boolean;
  shouldDisableClearable?: boolean;
  showValidation?: boolean;
  errs?: FieldValidationError[];
  disableTypeahead?: boolean;
  firstLoad?: boolean;
  stateOptions?: StateType[] | false;
  removeOption?: any[];
  // New props for FinCEN compatibility
  useStatesOnly?: boolean; // When true, only shows US states
  useTerritoriesOnly?: boolean; // When true, only shows territories
  useBothStatesAndTerritories?: boolean; // When true, shows both states and territories
  label?: string;
  dataTestId?: string;
  name?: string;
  isClearable?: boolean;
  defaultToFirst?: boolean;
  required?: boolean;
  id?: string;
  tabIndex?: number;
  variant?: 'outlined' | 'filled' | 'standard';
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
}

export function EnhancedStateSelectField({
  value: valueProp = '',
  onBlur = () => undefined,
  onChange = () => undefined,
  menuOption,
  shouldDisplayAbbreviation = true,
  shouldDisableClearable = false,
  showValidation,
  disabled = false,
  errs,
  disableTypeahead = false,
  stateOptions,
  removeOption = [],
  useStatesOnly = true,
  useTerritoriesOnly = false,
  useBothStatesAndTerritories = false,
  label = 'State/U.S. Territory',
  dataTestId = '',
  name = '',
  isClearable = false,
  defaultToFirst = false,
  required = false,
  id,
  tabIndex,
  ...props
}: EnhancedStateSelectFieldProps): JSX.Element {
  // Determine which options to use based on props
  const getAllOptions = useMemo(() => {
    let allOptions: StateType[] = [];

    if (useStatesOnly) {
      allOptions = states;
    } else if (useTerritoriesOnly) {
      allOptions = territories;
    } else if (useBothStatesAndTerritories) {
      allOptions = [...states, ...territories];
    } else {
      allOptions = states; // Default fallback
    }

    return allOptions.filter((item) => !removeOption.includes(item.abbreviation));
  }, [removeOption, useStatesOnly, useTerritoriesOnly, useBothStatesAndTerritories]);

  const statesByAbbr: { [key: string]: StateType } = useMemo(
    () => getAllOptions.reduce((prev, current) => ({ ...prev, [current.abbreviation]: current }), {}),
    [getAllOptions],
  );

  const [displayErrors, setDisplayError] = useState(showValidation);
  const [value, setValue] = useState<StateType | null>(null);

  useEffect(() => {
    if (value !== statesByAbbr[valueProp]) {
      setValue(statesByAbbr[valueProp] || null);
    }
  }, [valueProp, statesByAbbr]);

  useEffect(() => {
    if (showValidation) {
      setDisplayError(true);
    }
  }, [showValidation]);

  const filterStates = (options: StateType[], searchValue: FilterOptionsState<StateType>) => {
    const input = searchValue.inputValue.toLowerCase();

    if (input == '') {
      return options;
    }

    // Filter down state results that matches the first letter of the input
    const filteredOptions = options.filter((state: StateType) => {
      const stateResult = state.abbreviation.toLowerCase();
      return stateResult.includes(input) && stateResult[0] == input[0];
    });

    // Sort alphabetically
    return filteredOptions.sort((stateA, stateB) => {
      return stateA.abbreviation.localeCompare(stateB.abbreviation);
    });
  };

  const dropdownOptions: SelectOption[] = getAllOptions.map((e: StateType) => {
    return { name: e.name, value: e.abbreviation };
  });

  // Handle both onChange and menuOption callbacks for compatibility
  const handleSelectionChange = (selectedValue: string) => {
    if (onChange) {
      onChange(selectedValue);
    }
    if (menuOption) {
      menuOption(selectedValue);
    }
  };

  return (
    <>
      {!disableTypeahead ? (
        <Autocomplete
          autoComplete
          blurOnSelect
          disableClearable={shouldDisableClearable}
          clearOnEscape
          disabled={disabled}
          autoSelect
          autoHighlight
          filterOptions={filterStates}
          onBlur={onBlur}
          options={stateOptions && stateOptions?.length > 0 ? stateOptions : getAllOptions}
          getOptionLabel={(option: StateType) => (shouldDisplayAbbreviation ? option.abbreviation : option.name)}
          getOptionSelected={(option, state) => option.abbreviation === state.abbreviation}
          value={value}
          popupIcon={<ArrowDropDown />}
          onChange={(_, value) => {
            const selectedValue = value?.abbreviation || '';
            handleSelectionChange(selectedValue);
            setValue(value);
          }}
          renderInput={(param) => (
            <AgentNetTextInput
              showValidation={showValidation}
              errs={errs}
              variant="outlined"
              label={label}
              required={required}
              onChange={(e) => {
                handleSelectionChange(e.target.value);
              }}
              {...param}
              {...props}
              onBlur={(e) => {
                const container = stateOptions && stateOptions?.length > 0 ? stateOptions : getAllOptions;
                const isFound = container.filter(
                  (item: { abbreviation: string }) =>
                    item.abbreviation.toLowerCase().search(e.target.value.toLowerCase()) === 0,
                )[0];
                handleSelectionChange(isFound ? e.target.value : '');
                if (onBlur) onBlur(e);
              }}
              inputProps={{
                ...param.inputProps,
                'aria-label': 'state-field-input-select',
                maxLength: 2,
                title: value ? value.abbreviation : label,
              }}
              title={value ? value.abbreviation : label}
              InputLabelProps={{ shrink: true }}
            />
          )}
          id={id}
          data-testid={dataTestId}
        />
      ) : (
        <AgentNetDropdownSelector
          label={label}
          options={dropdownOptions}
          value={value?.abbreviation || valueProp}
          dropdowntype="outlined"
          disabled={disabled}
          required={required}
          title={value ? value.abbreviation : label}
          inputProps={{
            title: value ? value.abbreviation : label,
          }}
          menuOption={(option: string) => {
            handleSelectionChange(option);
          }}
          errs={errs}
          showValidation={showValidation}
          name={name}
          data-testid={dataTestId}
          isClearable={isClearable}
          defaultToFirst={defaultToFirst}
        />
      )}
    </>
  );
}

export default EnhancedStateSelectField;
